import { useEffect, useRef } from 'react'

/**
 * Custom hook that triggers a callback when a click event occurs outside of a specified element.
 * @param {React.RefObject} ref - The ref to the element.
 * @param {function} handler - The callback function to be called when a click event occurs outside of the element.
 */
export default function useClickAway(ref, handler) {
  const savedHandler = useRef()

  // Update saved handler if it changes.
  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    const eventListener = (event) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }

      savedHandler.current(event)
    }

    // Add event listener
    window.document.addEventListener('mousedown', eventListener)
    window.document.addEventListener('touchstart', eventListener)

    // Cleanup event listeners on unmount
    return () => {
      window.document.removeEventListener('mousedown', eventListener)
      window.document.removeEventListener('touchstart', eventListener)
    }
  }, [ref]) // Re-run if ref changes
}
